import { useEffect, useState, useCallback } from "react";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";

const TESTIMONIALS = [
  {
    name: "Sarah K.",
    city: "San Antonio, TX",
    quote:
      "My yard has never looked sharper. The crew shows up on time, every single visit, and the lines they cut are razor-clean. Worth every penny.",
    stars: 5,
  },
  {
    name: "Mike R.",
    city: "New Braunfels, TX",
    quote:
      "These guys treat my property like their own. Veteran-owned, fairly priced, and you can feel the pride in their work. Ironclad has earned every dollar.",
    stars: 5,
  },
  {
    name: "Jennifer T.",
    city: "Boerne, TX",
    quote:
      "Subscribed to the monthly yard tips and the difference is real — actionable advice, no fluff. The Ironclad team practices what they preach.",
    stars: 5,
  },
  {
    name: "David L.",
    city: "Schertz, TX",
    quote:
      "Hired them for a full property clean-up and I was floored. Showed up early, hauled everything off, zero surprises on the invoice. Pure professionalism.",
    stars: 5,
  },
  {
    name: "Carlos M.",
    city: "Alamo Heights, TX",
    quote:
      "Sharpest lawn on the block. The 10% veteran discount sealed the deal, but the work would have kept me regardless. These are my guys now.",
    stars: 5,
  },
  {
    name: "Aaron P.",
    city: "Cibolo, TX",
    quote:
      "Discipline, consistency, and curb appeal. The Ironclad standard is the real deal — and the crew makes you feel like part of the team.",
    stars: 5,
  },
];

export default function Testimonials({ compact = false, id = "testimonials" }) {
  const [idx, setIdx] = useState(0);
  const total = TESTIMONIALS.length;

  const go = useCallback((delta) => setIdx((i) => (i + delta + total) % total), [total]);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % total), 7000);
    return () => clearInterval(t);
  }, [total]);

  const current = TESTIMONIALS[idx];

  return (
    <section
      id={id}
      data-testid={`testimonials-${compact ? "compact" : "full"}`}
      className={
        compact
          ? "py-12 md:py-16 bg-background"
          : "py-24 md:py-32 bg-background border-t border-border"
      }
    >
      <div className="container-ic">
        {!compact && (
          <div className="max-w-3xl mb-12 md:mb-16">
            <div className="overline mb-4">
              <Star className="inline h-3.5 w-3.5 mr-2 -mt-0.5 fill-primary text-primary" />
              Customer Stories
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.95]">
              Real yards.
              <br />
              <span className="text-primary">Real reviews.</span>
            </h2>
            <p className="mt-6 text-muted-foreground text-lg max-w-xl">
              From first-time customers to long-running regulars, the Ironclad standard
              shows up in every cut.
            </p>
          </div>
        )}

        <div className="relative border border-primary/30 bg-card/30 p-8 md:p-14">
          {/* Decorative quote */}
          <Quote
            className="absolute top-6 left-6 md:top-8 md:left-8 h-10 w-10 md:h-14 md:w-14 text-primary/20"
            aria-hidden
          />

          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-1 mb-6">
              {Array.from({ length: current.stars }).map((_, i) => (
                <Star
                  key={i}
                  className="h-4 w-4 fill-primary text-primary"
                />
              ))}
            </div>

            <p
              key={idx}
              className="font-display text-xl md:text-3xl font-medium leading-snug tracking-tight text-foreground reveal"
              data-testid="testimonial-quote"
            >
              "{current.quote}"
            </p>

            <div className="mt-8 inline-flex flex-col items-center">
              <div className="h-px w-8 bg-primary mb-3" />
              <div
                className="font-display text-lg font-bold uppercase tracking-widest text-foreground"
                data-testid="testimonial-name"
              >
                {current.name}
              </div>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mt-1">
                {current.city}
              </div>
            </div>
          </div>

          {/* Prev/next controls */}
          <button
            onClick={() => go(-1)}
            aria-label="Previous testimonial"
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 border border-border bg-background/60 hover:border-primary hover:text-primary transition-colors flex items-center justify-center"
            data-testid="testimonial-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => go(1)}
            aria-label="Next testimonial"
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 border border-border bg-background/60 hover:border-primary hover:text-primary transition-colors flex items-center justify-center"
            data-testid="testimonial-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                className={`h-1.5 transition-all ${
                  i === idx
                    ? "w-8 bg-primary"
                    : "w-3 bg-border hover:bg-muted-foreground"
                }`}
                data-testid={`testimonial-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
